import React from 'react';
import type { WithOverlayProps } from '~/Features/StudyPage/Campus/Components/Overlay/WithOverlay';
import { WithOverlay } from '~/Features/StudyPage/Campus/Components/Overlay/WithOverlay';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { asHtmlString } from '~/Partials/Controls/Utils/asHtmlString';
import type {
    ImageBackgroundCallToActionLinkProps,
    ImageBackgroundCallToActionProps,
} from '~/Partials/ImageBackgroundCallToAction/ImageBackgroundCallToAction';
import { ImageBackgroundCallToAction } from '~/Partials/ImageBackgroundCallToAction/ImageBackgroundCallToAction';
import type { StrictOmit } from '~/Partials/Utils/typeUtils';
import { assertNever } from '~/Partials/Utils/typeUtils';
import styles from './BookMeetingSection.module.css';
import { BookMeetingGtmSubscription } from './GtmSubscription/BookMeetingGtmSubscription';
import type { BookMeetingOverlayContentProps } from './OverlayContent/BookMeetingOverlayContent';
import { BookMeetingOverlayContent } from './OverlayContent/BookMeetingOverlayContent';

interface ButtonWithOverlayCTAProps {
    type: 'button';
    label: string;
    overlayContent: StrictOmit<BookMeetingOverlayContentProps, 'onClose'>;
    translations: Pick<WithOverlayProps, 'closeLabel' | 'overlayLabel'>;
}

interface LinkCTAProps extends StrictOmit<ImageBackgroundCallToActionLinkProps, 'type'> {
    type: 'link';
}

export type BookMeetingSectionCTAProps = ButtonWithOverlayCTAProps | LinkCTAProps;

interface BookMeetingSectionProps extends Pick<ImageBackgroundCallToActionProps, 'image'> {
    id: string;
    callToAction: BookMeetingSectionCTAProps;
    teaser?: HtmlString;
    heading?: HtmlString;
}

export const BookMeetingSection: React.FC<React.PropsWithChildren<BookMeetingSectionProps>> = props => {
    const { id, callToAction, teaser, heading, ...restProps } = props;

    const renderCallToAction = () => {
        switch (callToAction.type) {
            case 'button':
                const { label, overlayContent, translations } = callToAction;

                return (
                    <>
                        <BookMeetingGtmSubscription formId={id} />
                        <WithOverlay
                            {...translations}
                            overlayContent={close => (
                                <BookMeetingOverlayContent {...overlayContent} onClose={close} formId={id} />
                            )}
                        >
                            {triggerButtonProps => (
                                <ImageBackgroundCallToAction
                                    callToAction={{ type: 'button', label, ...triggerButtonProps }}
                                    description={teaser}
                                    variant="prominent"
                                    heading={heading ?? asHtmlString('')}
                                    className={styles.ImageBackgroundSection}
                                    {...restProps}
                                />
                            )}
                        </WithOverlay>
                    </>
                );
            case 'link':
                return (
                    <ImageBackgroundCallToAction
                        callToAction={callToAction}
                        description={teaser}
                        variant="prominent"
                        heading={heading ?? asHtmlString('')}
                        className={styles.ImageBackgroundSection}
                        {...restProps}
                    />
                );

            default:
                return assertNever(callToAction);
        }
    };

    return (
        <div id={id} className={styles.BookMeetingSection}>
            {renderCallToAction()}
        </div>
    );
};
